import { ELEM_DATASET_DRILLDOWN_MIXIN_NAME } from './const';

export const ElemDatasetDrilldownMixin = {
    _name: ELEM_DATASET_DRILLDOWN_MIXIN_NAME,
    static: {
        $drilldownStateMeta: {}
    },
    data() {
        return {
            mixinDrilldownState: {}
        };
    },
    computed: {
        $drilldown() {
            return {
                state: this.mixinDrilldownState,
                dimensions: Object.values(this.mixinDrilldownState).flatMap(({ dimensions }) => dimensions),
                canPopAny: Object.values(this.mixinDrilldownState).some(({ canPop }) => canPop),
                canPushAny: Object.values(this.mixinDrilldownState).some(({ canPush }) => canPush),
                push: this.$drilldownPush,
                pop: this.$drilldownPop,
                setIndex: this.$drilldownSetIndex,
                getDimension: this.$drilldownGetDimension,
                hasQueryFilter: this.$drilldownHasQueryFilter,
                canPop: this.$drilldownCanPop,
                canPush: this.$drilldownCanPush
            };
        }
    },
    computedEditor: {
        /**
         *
         * @return {string[]}
         */
        drilldowns() {
            return this.$queryGetParams(['drilldown.names']);
        }
    },
    watch: {
        requests: {
            handler(requests) {
                this.$drilldownSetup(requests);
            }
        }
    },

    methods: {
        $drilldownSetup() {
            const { state, meta } = this.$drilldownGetState();
            this.mixinDrilldownState = state;
            this.$drilldownStateMeta = meta;
        },
        /**
         *
         * @param requests
         * @return {{ state: Record<string, import('@goodt-common/data').Drilldown>, meta: Record<string, { requests: import('@goodt-common/data').Request }> }}
         */
        $drilldownGetState(requests = this.requests) {
            return requests.filter(Boolean).reduce(
                (acc, request) => {
                    const {
                        query: { drilldown }
                    } = request;
                    // eslint-disable-next-line guard-for-in
                    for (const drilldownName in drilldown.models) {
                        acc.meta[drilldownName] = {
                            requests: (acc.meta[drilldownName]?.requests ?? []).concat(request)
                        };
                        acc.state = this.$drilldownUpdateState(acc.state, drilldown.models[drilldownName]);
                    }
                    return acc;
                },
                { state: {}, meta: {} }
            );
        },
        /**
         *
         * @param {string} drilldownName
         * @param {string|number|boolean} value
         */
        $drilldownPush(drilldownName, value) {
            const mixinDrilldownState = this.mixinDrilldownState[drilldownName];
            if (mixinDrilldownState == null) {
                return false;
            }

            if (mixinDrilldownState.canPush) {
                const { requests } = this.$drilldownStateMeta[drilldownName];
                /**
                 *
                 * @type {import('@goodt-common/data').Drilldown}
                 */
                const drilldownModel = requests.reduce(
                    (acc, { query: { drilldown } }) => drilldown.push(drilldownName, value),
                    null
                );
                this.mixinDrilldownState = this.$drilldownUpdateState(this.mixinDrilldownState, drilldownModel);
                this.$requestChanged(requests);
                return true;
            }

            return false;
        },
        /**
         *
         * @param {string} drilldownName
         */
        $drilldownPop(drilldownName) {
            const mixinDrilldownState = this.mixinDrilldownState[drilldownName];
            if (mixinDrilldownState == null) {
                return false;
            }
            if (mixinDrilldownState.canPop) {
                const { requests } = this.$drilldownStateMeta[drilldownName];
                /**
                 *
                 * @type {import('@goodt-common/data').Drilldown}
                 */
                const drilldownModel = requests.reduce(
                    (acc, { query: { drilldown } }) => drilldown.pop(drilldownName),
                    null
                );
                this.mixinDrilldownState = this.$drilldownUpdateState(this.mixinDrilldownState, drilldownModel);
                this.$requestChanged(requests);
                return true;
            }

            return false;
        },
        /**
         * @param {string} drilldownName
         * @param {number} index
         * @param {string|number|boolean} values
         */
        $drilldownSetIndex(drilldownName, index, values = []) {
            const mixinDrilldownState = this.mixinDrilldownState[drilldownName];
            if (mixinDrilldownState == null) {
                return false;
            }
            if (index === mixinDrilldownState.index || index < 0 || index > mixinDrilldownState.length - 1) {
                return false;
            }

            const { requests } = this.$drilldownStateMeta[drilldownName];
            const drilldownModel = requests.reduce(
                (acc, { query: { drilldown } }) => drilldown.setIndex(drilldownName, index, values),
                null
            );
            this.mixinDrilldownState = this.$drilldownUpdateState(this.mixinDrilldownState, drilldownModel);
            this.$requestChanged(requests);

            return true;
        },
        /**
         *
         * @param state
         * @param {import('@goodt-common/data').Drilldown} drilldown
         */
        $drilldownUpdateState(state, drilldown) {
            const { name, index, length, canPop, canPush, filters, dimension, dimensions} = drilldown;
            return {
                ...state,
                [name]: { name, index, length, canPop, canPush, filters, dimension, dimensions }
            };
        },
        /**
         *
         * @param {string} drilldownName
         * @return {boolean}
         */
        $drilldownCanPop(drilldownName) {
            return this.mixinDrilldownState?.[drilldownName]?.canPop ?? false;
        },
        /**
         *
         * @param {string} drilldownName
         * @return {boolean}
         */
        $drilldownCanPush(drilldownName) {
            return this.mixinDrilldownState?.[drilldownName]?.canPush ?? false;
        },
        /**
         *
         * @param {string} drilldownName
         * @return {string|null}
         */
        $drilldownGetDimension(drilldownName) {
            return this.mixinDrilldownState[drilldownName]?.dimension ?? null;
        },
        /**
         * if current drilldown dimension has runtime or static filters in query
         *
         * @param {string} drilldownName
         * @return {boolean}
         */
        $drilldownHasQueryFilter(drilldownName) {
            const dimension = this.mixinDrilldownState[drilldownName]?.dimension;
            if (dimension == null) {
                return false;
            }
            return this.$queryHasFilter(dimension);
        },
        /**
         * Add drilldown to current dimension state key replace
         *
         * @override {Elem.$storeCommit}
         * @param {Record<string, any>} internalStatePartial
         * @param {boolean|null} [hasMap=true]
         */
        $storeCommit(internalStatePartial, { hasMap = true } = {}) {
            // eslint-disable-next-line no-param-reassign
            internalStatePartial = this.drilldowns.reduce((state, drilldownName) => {
                if (drilldownName in state) {
                    const { [drilldownName]: value, ...rest } = state;
                    const { dimension } = this.mixinDrilldownState[drilldownName];
                    return {
                        ...rest,
                        [dimension]: value
                    };
                }
                return state;
            }, internalStatePartial);

            this.super().$storeCommit.call(this, internalStatePartial, { hasMap });
        }
    }
};
